/**
 * Benutzerliste (Einrichten-Neubau P4).
 *
 * Ersetzt die alte Users-Seite: keine Sofort-Toggles in Zellen, keine
 * versteckten Sonderregeln — eine Tabelle mit Filter (auch Chipnummern
 * findbar), Inaktive sichtbar, und der HR-Import mit echter Vorschau
 * (dryRun) und sichtbarem Ergebnis, das die Alt-Seite nie anzeigte.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Kopfzelle, Notiz, Tabelle, Zelle, Zustand } from '../../components/leitstand';
import { CsvDropzone, ImportField, FieldMapping, ImportResult } from '../../components/CsvDropzone';

export const ROLLEN_LABELS: Record<string, string> = {
  WORKER: 'Mitarbeiter',
  FOREMAN: 'Schichtleiter',
  ADMIN: 'Verwaltung',
  SUPERADMIN: 'Betriebsleitung',
  SYSTEM: 'System',
};

interface Benutzer {
  id: string;
  displayName: string;
  role: string;
  isActive: boolean;
  updatedAt: string;
  hatChip?: boolean;
  chipKurz?: string | null;
  rfidPrintedCode?: string | null;
  externalEmployeeId?: string | null;
  email?: string | null;
}

const IMPORT_FELDER: ImportField[] = [
  {
    key: 'externalEmployeeId',
    label: 'Personalnummer',
    required: true,
    description: 'Eindeutige ID aus dem HR-System',
  },
  { key: 'displayName', label: 'Anzeigename', required: false },
  {
    key: 'firstName',
    label: 'Vorname',
    required: false,
    description: 'Mindestens Vor- oder Nachname',
  },
  { key: 'lastName', label: 'Nachname', required: false },
  { key: 'email', label: 'E-Mail', required: false },
  {
    key: 'rfidPrintedCode',
    label: 'Chipnummer',
    required: false,
    description: 'Optional, darf leer sein',
  },
];

export default function BenutzerListe() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [suchtext, setSuchtext] = useState('');
  const [rolle, setRolle] = useState('');
  const [importOffen, setImportOffen] = useState(false);

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch('/api/einrichten/objekt/benutzer', { credentials: 'include' });
      if (!res.ok) throw new Error('Die Benutzerliste konnte nicht geladen werden');
      setBenutzer(((await res.json()).objekte ?? []) as Benutzer[]);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Benutzerliste konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void lade();
  }, [lade]);

  const importiere = async (
    data: Record<string, unknown>[],
    mapping: FieldMapping,
    options?: { dryRun?: boolean; applyUpdates?: boolean },
  ): Promise<ImportResult> => {
    const res = await fetch('/api/admin/users/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ data, mapping, options }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.error || 'Import fehlgeschlagen');
    }
    const result = await res.json();
    if (!options?.dryRun) void lade();
    return result;
  };

  const begriff = suchtext.trim().toLowerCase();
  const gefiltert = benutzer.filter((b) => {
    if (rolle && b.role !== rolle) return false;
    if (!begriff) return true;
    return (
      b.displayName.toLowerCase().includes(begriff) ||
      b.externalEmployeeId?.toLowerCase().includes(begriff) ||
      b.chipKurz?.toLowerCase().includes(begriff) ||
      b.rfidPrintedCode?.toLowerCase().includes(begriff) ||
      b.email?.toLowerCase().includes(begriff)
    );
  });

  return (
    <LeitstandRahmen
      titel="Benutzer"
      benutzer={user?.displayName}
      werkzeuge={
        <span className="flex gap-2">
          <button
            onClick={() => setImportOffen((o) => !o)}
            className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
          >
            HR-Import
          </button>
          <button
            onClick={() => navigate('/einrichten/benutzer/neu')}
            className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz"
          >
            Mitarbeiter anlegen
          </button>
        </span>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/einrichten" className="text-[13px] text-tinte-3 hover:text-tinte-2">
            ← Katalog
          </Link>
          <input
            type="text"
            value={suchtext}
            onChange={(e) => setSuchtext(e.target.value)}
            placeholder="Name, Personalnummer, Chip …"
            className="min-w-[220px] flex-1 rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 text-sm text-tinte placeholder:text-tinte-3 focus:border-akzent focus:outline-none sm:max-w-xs"
          />
          <select
            value={rolle}
            onChange={(e) => setRolle(e.target.value)}
            className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 text-sm text-tinte"
          >
            <option value="">Alle Rollen</option>
            {Object.entries(ROLLEN_LABELS).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {importOffen && (
          <Karte titel="HR-Import" marke="mit Vorschau — übernommen wird erst auf Knopf">
            <CsvDropzone
              fields={IMPORT_FELDER}
              onImport={importiere}
              showNameSplit
              onClose={() => setImportOffen(false)}
              title="Benutzer importieren"
              description="CSV oder Excel-Export aus dem HR-System. Die Vorschau zeigt, was passieren würde — übernommen wird erst nach Bestätigung."
            />
          </Karte>
        )}

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && benutzer.length === 0 && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Benutzer …</p>
        )}

        {!laden && !fehler && (
          <Karte titel="Benutzer" marke={`${gefiltert.length} von ${benutzer.length}`} tabelle>
            {gefiltert.length === 0 ? (
              <p className="py-6 text-center text-sm text-tinte-3">Kein Treffer im Filter.</p>
            ) : (
              <div className="overflow-x-auto">
                <Tabelle>
                  <thead>
                    <tr>
                      <Kopfzelle>Name</Kopfzelle>
                      <Kopfzelle>Rolle</Kopfzelle>
                      <Kopfzelle>Chip</Kopfzelle>
                      <Kopfzelle>Zustand</Kopfzelle>
                      <Kopfzelle rechts>Geändert</Kopfzelle>
                    </tr>
                  </thead>
                  <tbody>
                    {gefiltert.map((b, i) => {
                      const letzte = i === gefiltert.length - 1;
                      return (
                        <tr
                          key={b.id}
                          onClick={() => navigate(`/einrichten/benutzer/${b.id}`)}
                          className={`cursor-pointer hover:bg-flaeche-2 ${b.isActive ? '' : 'opacity-60'}`}
                        >
                          <Zelle letzte={letzte}>{b.displayName}</Zelle>
                          <Zelle letzte={letzte}>{ROLLEN_LABELS[b.role] ?? b.role}</Zelle>
                          <Zelle letzte={letzte}>
                            {b.hatChip ? (
                              <Zustand art="gut">angelernt</Zustand>
                            ) : (
                              <span className="font-zahl text-[11px] text-tinte-3">kein Chip</span>
                            )}
                          </Zelle>
                          <Zelle letzte={letzte}>
                            {b.isActive ? (
                              <Zustand art="gut">aktiv</Zustand>
                            ) : (
                              <span className="font-zahl text-[11px] text-tinte-3">
                                außer Betrieb
                              </span>
                            )}
                          </Zelle>
                          <Zelle rechts zahl letzte={letzte}>
                            {new Date(b.updatedAt).toLocaleDateString('de-DE', {
                              timeZone: 'Europe/Berlin',
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                            })}
                          </Zelle>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tabelle>
              </div>
            )}
          </Karte>
        )}
      </div>
    </LeitstandRahmen>
  );
}
